import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserCircle, CalendarClock, Zap, Minus, Plus } from "lucide-react";
import { Header } from "@/pages/Operations";
import { QuotaMeters } from "@/components/account/QuotaMeters";
import { ReferralCodes } from "@/components/account/ReferralCodes";
import { InvoiceList } from "@/components/account/InvoiceList";
import { ProfileEditor } from "@/components/account/ProfileEditor";
import { VerifyPanel } from "@/components/auth/VerifyPanel";
import { useAuth } from "@/context/AuthContext";
import { UpiCheckout } from "@/components/billing/UpiCheckout";
import { DataRights } from "@/components/account/DataRights";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

export default function Account() {
  const { user: authUser } = useAuth();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [units, setUnits] = useState(1);
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/account/summary").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const topup = async () => {
    setBusy(true);
    try { const r = await api.post("/billing/orders", { pack_id: "topup", topup_units: units }); setOrder(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start top-up"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-8 text-slate-500 font-mono text-sm animate-pulse">{t("Loading account…")}</div>;
  const sub = data.subscription, tp = data.topup;

  return (
    <div data-testid="account-page" className="p-5 sm:p-8 max-w-[1400px] mx-auto">
      <Header icon={UserCircle} eyebrow={t("My Account")} title={data.user.name} subtitle={`${data.user.email} · ${data.user.role}`} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div data-testid="account-subscription" className="lg:col-span-2 glass rounded-2xl p-5 flex flex-wrap items-center gap-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t("Current pack")}</div>
            <div className="font-display font-extrabold text-2xl">{sub ? sub.pack_name : t("None")}</div>
            {sub && <div data-testid="account-license" className={`text-[10px] font-mono uppercase ${sub.license === "copyright_free" ? "text-amber-300" : "text-slate-500"}`}>{t(sub.license === "copyright_free" ? "Copyright-Free · full commercial rights" : "Standard license")}</div>}
            {!sub && <Link to="/billing" data-testid="account-buy-pack" className="text-xs text-cyan-300 hover:underline">{t("Buy a prepaid pack →")}</Link>}
          </div>
          {sub && (
            <>
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t("Activated")}</div><div className="font-mono text-sm">{fmtDate(sub.starts_at)}</div></div>
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1"><CalendarClock size={11} /> {t("Next renewal")}</div><div data-testid="account-renewal" className="font-mono text-sm">{fmtDate(sub.expires_at)} <span className="text-slate-500">· {daysLeft(sub.expires_at)} {t("days")}</span></div></div>
              <Link to="/billing" className="ml-auto text-xs px-3 py-1.5 rounded-full border border-white/15 hover:bg-white/5">{t("Renew / upgrade")}</Link>
            </>
          )}
        </div>

        <div data-testid="topup-card" className="glass rounded-2xl p-5">
          <div className="font-display font-semibold text-sm flex items-center gap-2"><Zap size={15} className="text-amber-400" /> {t("Top-up")}</div>
          <div className="text-xs text-slate-500 mt-1">₹{tp.unit} {t("per extra video credit")} · {t("valid until your active pack expires")}.</div>
          <div className="flex items-center gap-3 mt-3">
            <button data-testid="topup-minus" onClick={() => setUnits((u) => Math.max(1, u - 1))} className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/5"><Minus size={13} /></button>
            <div className="text-center"><div data-testid="topup-amount" className="font-display font-extrabold text-xl">₹{(units * tp.unit).toLocaleString("en-IN")}</div><div className="text-[10px] font-mono text-slate-500">{units} {t(units > 1 ? "units" : "unit")}</div></div>
            <button data-testid="topup-plus" onClick={() => setUnits((u) => Math.min(50, u + 1))} className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/5"><Plus size={13} /></button>
            <button data-testid="topup-pay" onClick={topup} disabled={busy || !sub} className="ml-auto text-xs px-3 py-2 rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold disabled:opacity-40">{t("Pay via UPI")}</button>
          </div>
          {!sub && <div className="text-[10px] text-amber-400/80 mt-2">{t("Top-ups need an active pack.")}</div>}
        </div>
        <DataRights />
      </div>

      {data.renewal_due && sub && (
        <div data-testid="account-renewal-banner" className="mb-6 rounded-2xl p-4 border border-amber-500/30 bg-amber-500/5 flex items-center gap-3 text-sm">
          <CalendarClock size={18} className="text-amber-400" />
          <span className="flex-1">{sub.pack_name} — {t("Your pack renews on")} {fmtDate(sub.expires_at)} ({daysLeft(sub.expires_at)} {t("days")}). {t("Renew now to keep your quotas flowing.")}</span>
          <Link to={`/billing?pack=${sub.pack_id}&license=${sub.license || "standard"}`} data-testid="account-renew-now" className="text-xs px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold">{t("Renew via UPI")}</Link>
        </div>
      )}

      {order && <div className="mb-6"><UpiCheckout order={order} onClose={() => setOrder(null)} onSubmitted={() => { setOrder(null); load(); }} /></div>}

      <div className="mb-6"><QuotaMeters subscription={sub} unlimited={data.unlimited} whitelistUntil={data.whitelist_expires_at} /></div>

      <div className="mb-6"><VerifyPanel user={authUser || data.user} /></div>
      <div className="mb-6"><ProfileEditor user={authUser || data.user} onSaved={load} /></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <InvoiceList invoices={data.invoices} />
        <ReferralCodes referrals={data.referrals} slotsLeft={data.referral_slots_left} bonusDays={data.referral_bonus_days} onChange={load} />
      </div>
    </div>
  );
}
